import React, {useEffect, useState} from 'react';
import {bindActionCreators} from 'redux';
import Guid from 'guid';
import moment from 'moment';
import {useHistory} from 'react-router-dom';
import {connect} from 'react-redux';
import {useForm} from 'react-hook-form';
import {withTranslation} from 'react-i18next';

import {loadRestaurant, setDeliveryType, setOrderGuidCode, setOrderType} from '../../redux/actions/baseActions';
import {DeliveryType} from '../../constants/deliveryTypes';
import SearchLocationInput from '../../components/SearchLocationInput';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getLanguageById} from '../../helpers/language';
import {updateClientInfo} from '../../redux/actions/clientActions';
import IPosModal from '../../IPosModal';
import AreasService from '../../services/areas.service';
import {clearCostDelivery, setCostDelivery} from '../../redux/actions/costDeliveryActions';
import OptionTakeAway from './OptionTakeAway';
import OptionEatIn from './OptionEatIn';
import { getImageUrl } from "../../api/iposApi";
import {OrderType} from '../../constants/orderTypes';
import { loadBranchesChainByParentGuidCode, loadBranchInfo } from "../../redux/actions/branchActions";
import {convertDotNetTimeToMomentDate} from '../../helpers/dateTime';

const DeliveryTypeSwitcher = ({
  t,
  setDeliveryType,
  setOrderGuidCode,
  setOrderType,
  currentDeliveryType,
  clientInfo,
  currentLanguageId,
  updateClientInfo,
  branchInfo,
  setCostDelivery,
  clearCostDelivery,
  loadRestaurant,
  loadBranchInfo,
  loadBranchesChainByParentGuidCode,
}) => {
  const { register, watch, errors, handleSubmit, getValues } = useForm();
  const [currentType, setCurrentType] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressError, setAddressError] = useState(null);
  //const [addressAllowed, setAddressAllowed] = useState(false);
  const [addressArea, setAddressArea] = useState(null);
  const lang = getLanguageById(currentLanguageId);
  const history = useHistory();

  const getTodayWorkingHours = () => {
    const datesObj = branchInfo.DeliveryTimes.find((dateObj) => {
      return dateObj.day_of_week === moment().day();
    });

    if (!datesObj || !datesObj.start_dt || !datesObj.end_dt) {
      return [null, null];
    }

    const start = convertDotNetTimeToMomentDate(datesObj.start_dt);
    const end = convertDotNetTimeToMomentDate(datesObj.end_dt);

    return [
      moment().set({ hour: start.hour(), minute: start.minute() }),
      moment().set({ hour: end.hour(), minute: end.minute() }),
    ];
  };

  const openBranchesPopup = async () => {
    let closeModalFlg = false;
    if (!branchInfo.is_master_chain_flg) return false;

    const branches = await loadBranchesChainByParentGuidCode(
      branchInfo.branch_guid_code
    );

    return new Promise((resolve) => {
      IPosModal.branchesList({
        branches: branches,
        showAllBranch: true,
        history: history,
        onOk: async (guidCode) => {
          branchInfo = await loadBranchInfo(guidCode);
          resolve(false);
        },
        onClose: () => {
          resolve(true);
        },
      });
    });
  };

  const onSelectType = async (type) => {
    if (!branchInfo || !branchInfo.branch_guid_code) {
      return;
    }

    if (currentType === type) {
      return;
    }

    if (
      [DeliveryType.DELIVERY, DeliveryType.TA, DeliveryType.EAT_IN].includes(
        type
      ) &&
      !branchInfo.IsWorkingDelivery
    ) {
      const [startTime, endTime] = getTodayWorkingHours();
      let message = null;
      if (!startTime && !endTime) {
        message = t("dialogs.restaurant_closed_day_off");
      }

      if (startTime && moment.duration(moment().diff(startTime)) < 0) {
        message = t("dialogs.restaurant_closed_before_working_hour", {
          start: startTime.format("HH:mm"),
          end: endTime.format("HH:mm"),
        });
      }

      if (endTime && moment.duration(moment().diff(endTime)) > 0) {
        message = t("dialogs.restaurant_closed_after_working_hour");
      }

      IPosModal.alert({
        title: "",
        message: message,
        okButtonText: t("buttons.ok"),
        onOk: () => {},
      });

      return;
    }

    const messagesToShow = branchInfo.MessagesInfo.filter(
      (msg) => msg.item_relations.length === 0
    );

    if (
      [DeliveryType.DELIVERY, DeliveryType.TA, DeliveryType.EAT_IN].includes(
        type
      ) &&
      messagesToShow.length > 0 &&
      !currentType
    ) {
      for (let i = 0; i < messagesToShow.length; i++) {
        if (messagesToShow[i].web_image_name_txt) {
          IPosModal.adImageModal({
            imagePath: messagesToShow[i].web_image_name_txt,
            onOk: () => {
              if (i === messagesToShow.length - 1) {
                navigateAccordingToType(type);
              }
            },
          });
        } else {
          IPosModal.alert({
            title: "",
            message: messagesToShow[i].message_txt,
            okButtonText: t("buttons.ok"),
            onOk: () => {
              if (i === messagesToShow.length - 1) {
                navigateAccordingToType(type);
              }
            },
          });
        }
      }
      return;
    }

    navigateAccordingToType(type);
  };

  const navigateAccordingToType = (type) => {
    setCurrentType(type);

    if (
      [DeliveryType.MENU, DeliveryType.TA, DeliveryType.EAT_IN].includes(type)
    ) {
      setDeliveryType(type);
      history.push(`/${branchInfo.branch_guid_code}/order`);
    }
  };

  useEffect(() => {
    clearCostDelivery();
    setOrderGuidCode((Guid.create()).value);
  }, []);

  useEffect(() => {
    if (!selectedAddress) {
      setAddressArea(null);
      return;
    }

    AreasService.validateAddress(
      selectedAddress,
      branchInfo.branch_guid_code
    ).then((res) => {
      if (res) {
        setAddressArea(res);
      } else {
        setAddressArea(null);
        IPosModal.alert({
          title: "",
          message: t("dialogs.not_supported_area_error"),
          okButtonText: t("buttons.ok"),
          cancelButtonText: t("buttons.cancel"),
          onOk: () => {},
        });
      }
    });
  }, [selectedAddress]);

  const onAddressError = (error) => {
    setAddressError(error);
  };

  const chooseDeliveryType = () => {
    if (currentType === DeliveryType.DELIVERY && addressError) {
      IPosModal.alert({
        title: "",
        message: addressError,
        okButtonText: t("buttons.ok"),
        cancelButtonText: t("buttons.cancel"),
        onOk: () => {},
      });

      return;
    }

    if (
      currentType === DeliveryType.DELIVERY &&
      (!selectedAddress || !addressArea || addressArea === "undefined")
    ) {
      IPosModal.alert({
        title: "",
        message:
          addressArea === "undefined"
            ? t("dialogs.delivery_form_not_full_error")
            : t("dialogs.not_supported_area_error"),
        okButtonText: t("buttons.ok"),
        cancelButtonText: t("buttons.cancel"),
        onOk: () => {},
      });

      return;
    }

    if (currentType === DeliveryType.DELIVERY) {
      const addressDetails = getValues(["apartment", "flor", "addressNote"]);
      const address = {
        formatted_address: selectedAddress.formatted_address,
        lat: selectedAddress.geometry.location.lat(),
        lng: selectedAddress.geometry.location.lng(),
        place_id: selectedAddress.place_id,
      };
      const newClientInfo = Object.assign(
        {},
        clientInfo,
        { address: address },
        addressDetails,
        { addressArea: addressArea }
      );
      updateClientInfo(newClientInfo);

      const deliveryPrice =
        addressArea === "undefined" || !addressArea
          ? 0
          : addressArea.delivery_price;
      setCostDelivery(+deliveryPrice);
    }

    if (currentType) {
      setDeliveryType(currentType);
      history.push(`/${branchInfo.branch_guid_code}/order`);
    }
  };

  let deliveryAdditionalClass = "";
  let taAdditionalClass = "";
  let menuAdditionalClass = "";

  if (!currentType) {
    deliveryAdditionalClass = "";
    taAdditionalClass = "";
    menuAdditionalClass = "";
  }

  if (currentType === DeliveryType.DELIVERY) {
    deliveryAdditionalClass = "details";
    taAdditionalClass = "hide";
    menuAdditionalClass = "hide";
  }

  if (currentType === DeliveryType.TA || currentType === DeliveryType.EAT_IN) {
    deliveryAdditionalClass = "hide";
    taAdditionalClass = "details";
    menuAdditionalClass = "hide";
  }

  if (currentType === DeliveryType.MENU) {
    deliveryAdditionalClass = "hide";
    taAdditionalClass = "hide";
    menuAdditionalClass = "details";
  }

  return (
    <div className="space-evenly-container">
      <div className="logo">
        { branchInfo && branchInfo.LogoImageName && (<img
              src={getImageUrl(branchInfo.LogoImageName)}
              className="img-logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
          }}/>
        )}        
      </div>

      <div className={"delivery-type-switcher"}>
        {branchInfo.ShowDelivery && (
          <div
            className={`switcher-option delivery-option ${deliveryAdditionalClass}`}
            onClick={(e) => {
              setOrderType(OrderType.ASAP);
              onSelectType(DeliveryType.DELIVERY);
            }}
          >
            {currentType === DeliveryType.DELIVERY && (
              <div
                className={"close-delivery"}
                onClick={() => {
                  window.location.reload();
                }}
              >
                <FontAwesomeIcon icon="times" />
              </div>
            )}

            <div className={"title"}>
              {t("delivery_type_switcher.delivery.title")}
            </div>

            <div className={"switcher-logo"}>&nbsp;</div>

            <div className={"details-content"}>
              <div className={"address-selector"}>
                <div className={"label"}>
                  {t("delivery_type_switcher.delivery.select_address")}
                </div>
                <div className={"delivery-address-block"}>
                  <div className={"address-details"}>
                    <SearchLocationInput
                      placeholder={t(
                        "delivery_type_switcher.delivery.address_placeholder"
                      )}
                      language={getLanguageById(currentLanguageId)}
                      onAddressSelect={setSelectedAddress}
                      onError={onAddressError}
                    />
                    {selectedAddress &&
                      addressArea &&
                      addressArea !== "undefine" && (
                        <form className={"client-form"} onSubmit={() => {}}>
                          <div className={"form-input"}>
                            {/*<label htmlFor='apartment'>Apartment</label>*/}
                            <input
                              type="number"
                              name="apartment"
                              placeholder={t("client_details.apartment")}
                              ref={register}
                            />
                          </div>
                          <div className={"form-input"}>
                            {/*<label htmlFor='flor'>Flor</label>*/}
                            <input
                              type="number"
                              name="flor"
                              placeholder={t("client_details.flor")}
                              ref={register}
                            />
                          </div>
                          <div className={"form-input"}>
                            {/*<label htmlFor='addressNote'>{t('client_details.address_note')}</label>*/}
                            <textarea
                              name="addressNote"
                              placeholder={t("client_details.address_note")}
                              ref={register}
                            />
                          </div>
                        </form>
                      )}
                  </div>
                  <div className={"start-btn"} onClick={chooseDeliveryType}>
                    <FontAwesomeIcon
                      icon={
                        lang == "he"
                          ? "arrow-alt-circle-left"
                          : "arrow-alt-circle-right"
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {branchInfo.ShowTakeAway && (
          <OptionTakeAway
            chooseDeliveryType={chooseDeliveryType}
            onSelectType={async (type) => {
              const closeModalFlg = await openBranchesPopup();
              if (!closeModalFlg) {
                setOrderType(OrderType.ASAP);
                onSelectType(type);
              }
            }}
            taAdditionalClass={taAdditionalClass}
            branchInfo={branchInfo}
          />
        )}

        {branchInfo.ShowTakeAway &&
          (branchInfo.use_pin_pad_flg === 1 || branchInfo.ShowEatIn) && (
            <OptionEatIn
              chooseDeliveryType={chooseDeliveryType}
              onSelectType = { async (type) => {
                  const closeModalFlg = await openBranchesPopup();
                  if (!closeModalFlg) {
                    setOrderType(null);
                    onSelectType(type);
                  }
              }}
              taAdditionalClass={taAdditionalClass}
              branchInfo={branchInfo}
            />
          )}

        {branchInfo.ShowMenuInfo && (
          <div
            className={`switcher-option menu-option ${menuAdditionalClass}`}
            onClick={() => {
              onSelectType(DeliveryType.MENU);
            }}
          >
            <div className={"title"}>
              {t("delivery_type_switcher.menu.title")}
            </div>
            <div className={"switcher-logo"}>&nbsp;</div>
            <div className={"details-content"}>
              <div className={"start-btn-block"}>
                <div className={"start-btn"} onClick={chooseDeliveryType}>
                  <FontAwesomeIcon icon="sign-in-alt" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    branchInfo,
    orderGuidCode,
    currentDeliveryType,
    currentLanguageId,
    clientInfo,
    costDelivery
  } = state;

  return {
    branchInfo,
    orderGuidCode,
    currentDeliveryType,
    currentLanguageId,
    clientInfo,
    costDelivery
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setDeliveryType,
      setOrderGuidCode,
      setOrderType,
      updateClientInfo,
      setCostDelivery,
      clearCostDelivery,
      loadRestaurant,
      loadBranchInfo,
      loadBranchesChainByParentGuidCode
    },
    dispatch,
  );

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(DeliveryTypeSwitcher));
